import React, { useState } from 'react';
import { adminCarCreationService, adminCarService } from '../../../services/adminService';

const CreateCarModal = ({ inspection, onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [carCreated, setCarCreated] = useState(false);
  const [createdCarId, setCreatedCarId] = useState(null);
  const [showMediaUpload, setShowMediaUpload] = useState(false);

  const handleCreateCar = async () => {
    if (!window.confirm('Create car listing from this inspection?')) return;
    try {
      setProcessing(true);
      const result = await adminCarCreationService.createFromInspection(inspection.id);
      console.log('✅ Car created:', result);
      const carId = result?.car?.id || result?.data?.id || result?.id;
      setCreatedCarId(carId);
      setCarCreated(true);
    } catch (err) {
      console.error('Car creation error:', err);
      alert('Failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleDone = () => {
    alert('✅ Car created successfully! Moving to inventory stage.');
    onSuccess();
  };

  if (showMediaUpload && createdCarId) {
    return (
      <MediaUploadModal
        carId={createdCarId}
        inspection={inspection}
        onClose={() => { setShowMediaUpload(false); }}
        onDone={handleDone}
      />
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🚗 Create Car Listing</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="car-preview">
            <h3>
              {inspection.sell_request?.brand} {inspection.sell_request?.model} {inspection.sell_request?.year}
            </h3>
            <p>📍 {inspection.city} • 🛣️ {Number(inspection.sell_request?.km_driven || 0).toLocaleString()} km</p>
            <div className="price-info" style={{ marginTop: '12px' }}>
              <label>💰 Agreed Price:</label>
              <span style={{ marginLeft: '8px', fontWeight: 'bold', fontSize: '18px' }}>
                ₹{Number(inspection.estimated_price || 0).toLocaleString()}
              </span>
            </div>
            {inspection.inspection_report && (
              <div style={{ marginTop: '8px' }}>
                <label>📊 Inspection Score:</label>
                <span style={{ marginLeft: '8px', fontWeight: 'bold' }}>
                  {inspection.inspection_report.overall_score}/10
                </span>
              </div>
            )}
            <div style={{ marginTop: '12px', padding: '8px', background: '#f8f9fa', borderRadius: '6px', fontSize: '12px', color: '#666' }}>
              <p>✅ Seller status: <strong style={{ color: '#28a745' }}>Price Accepted</strong></p>
              <p>📋 Inspection: <strong>#{inspection.id}</strong> ({inspection.booking_reference})</p>
            </div>
          </div>

          {!carCreated ? (
            <p style={{ marginTop: '12px', color: '#555', fontSize: '13px', lineHeight: '1.5' }}>
              This will create a new car listing from the inspection details. The car will be
              in draft state and ready for inventory preparation.
            </p>
          ) : (
            <div style={{ marginTop: '14px', padding: '12px 16px', background: '#f0fff4', border: '1px solid #c6f6d5', borderRadius: '8px' }}>
              <p style={{ fontWeight: 700, color: '#28a745', marginBottom: '4px', fontSize: '14px' }}>✅ Car created successfully!</p>
              <p style={{ fontSize: '13px', color: '#555' }}>
                You can optionally add images & video now, or skip and proceed to the next step.
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {!carCreated ? (
            <>
              <button className="btn-modal-cancel" onClick={onClose} disabled={processing}>
                Cancel
              </button>
              <button className="btn-modal-submit" onClick={handleCreateCar} disabled={processing}>
                {processing ? '⏳ Creating…' : '🚗 Create Car'}
              </button>
            </>
          ) : (
            <>
              <button className="btn-modal-cancel" onClick={handleDone}>
                Skip & Continue →
              </button>
              <button
                className="btn-modal-submit"
                style={{ background: '#6f42c1', borderColor: '#6f42c1' }}
                onClick={() => setShowMediaUpload(true)}
              >
                🖼️ Add Media (Optional)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Media Upload Modal ────────────────────────────────────────────────────────
const MediaUploadModal = ({ carId, inspection, onClose, onDone }) => {
  const [images,         setImages]         = useState([]);
  const [exteriorImages, setExteriorImages] = useState([]);
  const [interiorImages, setInteriorImages] = useState([]);
  const [interiorVideo,  setInteriorVideo]  = useState(null);
  const [uploading,      setUploading]      = useState(false);
  const [previews,       setPreviews]       = useState({ images: [], exterior: [], interior: [] });

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'images')         { setImages(files);         setPreviews(p => ({ ...p, images:   files.map(f => URL.createObjectURL(f)) })); }
    if (type === 'exterior_images'){ setExteriorImages(files); setPreviews(p => ({ ...p, exterior: files.map(f => URL.createObjectURL(f)) })); }
    if (type === 'interior_images'){ setInteriorImages(files); setPreviews(p => ({ ...p, interior: files.map(f => URL.createObjectURL(f)) })); }
    if (type === 'interior_video') { setInteriorVideo(e.target.files[0] || null); }
  };

  const handleUpload = async () => {
    const hasAny = images.length || exteriorImages.length || interiorImages.length || interiorVideo;
    if (!hasAny) { onDone(); return; }
    try {
      setUploading(true);
      await adminCarService.updateCar(carId, {
        images,
        exterior_images: exteriorImages,
        interior_images: interiorImages,
        ...(interiorVideo ? { interior_video: interiorVideo } : {}),
      });
      alert('✅ Media uploaded successfully!');
      onDone();
    } catch (err) {
      alert('Media upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 640, width: '95vw' }}
      >
        <div className="modal-header">
          <div>
            <h2>🖼️ Add Media — Car #{carId}</h2>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
              {inspection.sell_request?.brand} {inspection.sell_request?.model} {inspection.sell_request?.year} &nbsp;|&nbsp; Optional step
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Main Images */}
          <MediaSection
            label="📸 Main Images"
            accept="image/*"
            multiple
            previews={previews.images}
            onChange={e => handleFileChange(e, 'images')}
            count={images.length}
          />

          {/* Exterior Images */}
          <MediaSection
            label="🚗 Exterior Images"
            accept="image/*"
            multiple
            previews={previews.exterior}
            onChange={e => handleFileChange(e, 'exterior_images')}
            count={exteriorImages.length}
          />

          {/* Interior Images */}
          <MediaSection
            label="🪑 Interior Images"
            accept="image/*"
            multiple
            previews={previews.interior}
            onChange={e => handleFileChange(e, 'interior_images')}
            count={interiorImages.length}
          />

          {/* Interior Video */}
          <div>
            <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>
              🎬 Interior Video
            </label>
            <input
              type="file"
              accept="video/mp4,video/webm,video/ogg,video/quicktime"
              onChange={e => handleFileChange(e, 'interior_video')}
              style={{ display: 'block', marginBottom: 6 }}
            />
            {interiorVideo && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#28a745', fontWeight: 600 }}>
                  ✅ {interiorVideo.name} ({(interiorVideo.size / 1024 / 1024).toFixed(1)} MB)
                </span>
                <button
                  onClick={() => setInteriorVideo(null)}
                  style={{ background: '#dc354520', color: '#dc3545', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                >
                  ✕ Clear
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-modal-cancel" onClick={onDone} disabled={uploading}>
            Skip
          </button>
          <button className="btn-modal-submit" onClick={handleUpload} disabled={uploading}>
            {uploading ? '⏳ Uploading…' : '📤 Upload & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── MediaSection helper ───────────────────────────────────────────────────────
const MediaSection = ({ label, accept, multiple, previews, onChange, count }) => (
  <div>
    <label style={{ fontWeight: 700, fontSize: 13, display: 'block', marginBottom: 6 }}>{label}</label>
    <input type="file" accept={accept} multiple={multiple} onChange={onChange} style={{ display: 'block', marginBottom: 6 }} />
    <small style={{ color: '#888', fontSize: 12 }}>{count} file(s) selected</small>
    {previews.length > 0 && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {previews.map((src, i) => (
          <img
            key={i} src={src} alt={`preview-${i}`}
            style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, border: '2px solid #e0e0e0' }}
          />
        ))}
      </div>
    )}
  </div>
);

export default CreateCarModal;